import type { GameCode } from '../../entities/room/types';

const ROULETTE_RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

export type ConsensusBet = {
  user?: { id?: number | string; user_id?: number | string; userId?: number | string; username?: string; name?: string };
  tab?: string;
  pick?: string | number;
  rawPick?: string | number;
  amount?: number;
  currency?: string;
};

/**
 * Every derived market a single pick lands in.
 *
 * A R$ 50 bet on last-digit "8" is Big money AND Even money — the crowd bars
 * have to say so. The backend's `breakdown.bars` only count bets placed on
 * those markets *directly*, which is why a round holding R$ 252 (mostly on
 * exact numbers) reported "R$ 0 · 0 people" on odd/even while claiming
 * "100% Big" off a lone R$ 10 side bet.
 *
 * Returns [] for picks that cover no derived market — roulette's zero, sicbo
 * combos/pairs, and any pick we cannot classify. That money is real, so it
 * shows up as the bar's uncovered remainder rather than being silently
 * dropped from the denominator.
 */
export function betMarketKeys(game: GameCode, _tab: string | undefined, pick: string | number | undefined): string[] {
  const raw = String(pick ?? '').trim().toLowerCase();
  if (!raw) return [];
  if (game === 'lastdigit') {
    if (/^[a-z]/.test(raw)) return ['big', 'small', 'odd', 'even'].includes(raw) ? [raw] : [];
    const digit = Number(raw);
    if (!Number.isInteger(digit) || digit < 0 || digit > 9) return [];
    return [digit >= 5 ? 'big' : 'small', digit % 2 === 1 ? 'odd' : 'even'];
  }
  if (game === 'sicbo') {
    if (raw === 'big' || raw === 'small') return [raw];
    // Sum bets carry their own big/small side: 4–10 small, 11–17 big.
    const sum = /^s(\d{1,2})$/.exec(raw);
    if (sum) {
      const value = Number(sum[1]);
      if (value < 4 || value > 17) return [];
      return [value >= 11 ? 'big' : 'small'];
    }
    return [];
  }
  if (game === 'roulette') {
    if (['red', 'black', 'odd', 'even', 'hi', 'lo'].includes(raw)) return [raw];
    const num = Number(raw);
    // Zero belongs to no even-money market — it is the house edge, so it stays
    // out of every numerator and surfaces as uncovered money instead.
    if (!Number.isInteger(num) || num < 1 || num > 36) return [];
    return [ROULETTE_RED.has(num) ? 'red' : 'black', num % 2 === 1 ? 'odd' : 'even', num >= 19 ? 'hi' : 'lo'];
  }
  return [];
}

export type ConsensusTotals = {
  money: Map<string, number>;
  /** Distinct bettors per market — someone on both 2 and 4 is one Small player. */
  people: Map<string, Set<string>>;
  /** Distinct bettors across the whole round: the denominator for "people". */
  allPeople: Set<string>;
  /** Sum of the classified currency's stakes: the denominator for "money". */
  totalAmount: number;
};

function betPersonKey(bet: ConsensusBet, index: number) {
  const user = bet.user || {};
  // A guest viewer gets every bettor anonymised to id 0 with a distinct
  // pseudo-handle. Trusting the id there collapsed the whole room into one
  // person ("3 bets · 1 person"), so 0 counts as "no id" and the handle wins.
  const text = String(user.id ?? user.user_id ?? user.userId ?? '').trim();
  // Zero is the anonymised sentinel, not an account; anything else non-empty
  // is a real id (numeric today, opaque strings stay supported).
  const id = text && Number(text) !== 0 ? text : '';
  if (id) return id;
  const name = String(user.username || user.name || '').trim();
  // "Player" is the anonymised display name every guest bet shares — it
  // identifies nobody, so fall through to a per-row key instead.
  if (name && name.toLowerCase() !== 'player') return name;
  return `anon:${index}`;
}

/**
 * Recompute the crowd bars from the round's own bet list.
 *
 * Only trustworthy when the list is complete — a truncated "latest 50" would
 * under-report the very whales the bars exist to surface, so callers must fall
 * back to the backend aggregates in that case.
 */
export function deriveConsensus(game: GameCode, bets: ConsensusBet[], currency = 'BRL'): ConsensusTotals {
  const money = new Map<string, number>();
  const people = new Map<string, Set<string>>();
  const allPeople = new Set<string>();
  let totalAmount = 0;
  bets.forEach((bet, index) => {
    // The bars render one currency's breakdown; mixing USDT stakes into the
    // same numerator would quietly inflate a side by the exchange rate.
    if (String(bet.currency || 'BRL').toUpperCase() !== currency.toUpperCase()) return;
    const amount = Number(bet.amount || 0);
    if (!(amount > 0)) return;
    const who = betPersonKey(bet, index);
    allPeople.add(who);
    totalAmount += amount;
    betMarketKeys(game, bet.tab, bet.pick ?? bet.rawPick).forEach((key) => {
      money.set(key, (money.get(key) || 0) + amount);
      const set = people.get(key) || new Set<string>();
      set.add(who);
      people.set(key, set);
    });
  });
  return { money, people, allPeople, totalAmount };
}

/** Overlay derived totals onto the backend's bar list, keeping its order. */
export function applyConsensus<T extends { key?: string; count?: number; bettors?: number; amount?: number }>(
  bars: T[],
  consensus: ConsensusTotals | null,
): T[] {
  if (!consensus) return bars;
  return bars.map((bar) => {
    const heads = consensus.people.get(String(bar.key || '').toLowerCase())?.size || 0;
    return {
      ...bar,
      amount: consensus.money.get(String(bar.key || '').toLowerCase()) || 0,
      bettors: heads,
      count: heads,
    };
  });
}
